import React, { useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { Button } from './ui/button';
import {
  ArrowRight, Check,
  // icons referenced from moduleLandingData
  ShoppingCart, Package, Calculator, UserCog, FolderKanban, Headphones,
  Fingerprint, Building2, Truck, KeyRound, ClipboardList,
  FileText, FileCheck, TrendingUp, Users, Zap, BarChart3, ScanBarcode,
  Bell, RefreshCw, BookOpen, Repeat, PieChart, Receipt, Lock,
  DollarSign, Calendar, Briefcase, GanttChart, Clock, Inbox, Timer,
  MessageSquare, Bot, Star, QrCode, ShieldCheck, Map, CreditCard,
  Smartphone, ClipboardCheck, Globe, Share2
} from 'lucide-react';

const iconMap = {
  ShoppingCart, Package, Calculator, UserCog, FolderKanban, Headphones,
  Fingerprint, Building2, Truck, KeyRound, ClipboardList,
  FileText, FileCheck, TrendingUp, Users, Zap, BarChart3, ScanBarcode,
  Bell, RefreshCw, BookOpen, Repeat, PieChart, Receipt, Lock,
  DollarSign, Calendar, Briefcase, GanttChart, Clock, Inbox, Timer,
  MessageSquare, Bot, Star, QrCode, ShieldCheck, Map, CreditCard,
  Smartphone, ClipboardCheck, Globe, Share2
};

const accentClasses = {
  green: { bg: 'bg-green-600', hover: 'hover:bg-green-700', text: 'text-green-600', soft: 'bg-green-100', badgeBg: 'bg-green-600/20', badgeBorder: 'border-green-600/30', badgeText: 'text-green-400' },
  blue: { bg: 'bg-blue-600', hover: 'hover:bg-blue-700', text: 'text-blue-600', soft: 'bg-blue-100', badgeBg: 'bg-blue-600/20', badgeBorder: 'border-blue-600/30', badgeText: 'text-blue-400' },
  purple: { bg: 'bg-purple-600', hover: 'hover:bg-purple-700', text: 'text-purple-600', soft: 'bg-purple-100', badgeBg: 'bg-purple-600/20', badgeBorder: 'border-purple-600/30', badgeText: 'text-purple-400' },
  orange: { bg: 'bg-orange-600', hover: 'hover:bg-orange-700', text: 'text-orange-600', soft: 'bg-orange-100', badgeBg: 'bg-orange-600/20', badgeBorder: 'border-orange-600/30', badgeText: 'text-orange-400' },
  red: { bg: 'bg-red-600', hover: 'hover:bg-red-700', text: 'text-red-600', soft: 'bg-red-100', badgeBg: 'bg-red-600/20', badgeBorder: 'border-red-600/30', badgeText: 'text-red-400' },
  cyan: { bg: 'bg-cyan-600', hover: 'hover:bg-cyan-700', text: 'text-cyan-600', soft: 'bg-cyan-100', badgeBg: 'bg-cyan-600/20', badgeBorder: 'border-cyan-600/30', badgeText: 'text-cyan-400' }
};

const ModulePage = ({ data }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const accent = accentClasses[data.accent] || accentClasses.green;
  const HeroIcon = iconMap[data.icon];

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Cloudbase ${data.name}`,
    description: data.hero.subtitle,
    serviceType: data.tagline,
    provider: {
      '@type': 'Organization',
      name: 'Cloudbase',
      url: 'https://cloudbase.cl'
    },
    areaServed: ['Chile', 'México', 'Argentina', 'Colombia', 'Perú', 'Brasil', 'España', 'Estados Unidos'],
    offers: {
      '@type': 'Offer',
      priceCurrency: 'USD',
      price: '39.00',
      description: 'Desde 39 USD por usuario/mes. Prueba gratuita 14 días.'
    }
  };

  return (
    <>
      <SEOHead
        title={data.seo.title}
        description={data.seo.description}
        keywords={data.seo.keywords}
        path={`/${data.slug}`}
        breadcrumbs={[
          { name: 'Inicio', item: '/' },
          { name: 'Productos', item: '/#modulos' },
          { name: data.name, item: `/${data.slug}` }
        ]}
        extraSchemas={[productSchema]}
      />
      <div className="min-h-screen bg-white" data-testid={`module-page-${data.slug}`}>
        <Header />

        {/* Hero */}
        <section className="pt-32 pb-20 bg-black relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900 to-black opacity-90"></div>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-5xl mx-auto text-center">
              <div className={`inline-flex items-center gap-2 mb-6 px-4 py-2 ${accent.badgeBg} border ${accent.badgeBorder} ${accent.badgeText} rounded-full text-sm font-semibold`}>
                {HeroIcon && <HeroIcon className="w-4 h-4" />}
                {data.tagline}
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8 leading-tight text-white">
                {data.hero.title}
              </h1>
              <p className="text-xl sm:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed font-light">
                {data.hero.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
                <Button
                  size="lg"
                  className={`${accent.bg} ${accent.hover} text-white px-10 py-7 text-base rounded font-semibold transition-all duration-200`}
                  onClick={() => (window.location.href = '/demo')}
                  data-testid={`${data.slug}-cta-demo`}
                >
                  Solicitar Demo
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-black px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                  onClick={() => (window.location.href = '/demo')}
                  data-testid={`${data.slug}-cta-trial`}
                >
                  Empezar Prueba Gratuita
                </Button>
              </div>
              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400">
                {data.hero.bullets.map((b, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-32 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-5xl sm:text-6xl font-bold mb-6 text-gray-900">
                Funcionalidades de {data.name}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
                Todo lo que necesitas, integrado en la plataforma Cloudbase.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {data.features.map((f, i) => {
                const Icon = iconMap[f.icon];
                return (
                  <div
                    key={i}
                    className="bg-white border border-gray-200 p-8 rounded-lg hover:shadow-lg transition-shadow"
                    data-testid={`${data.slug}-feature-${i}`}
                  >
                    <div className={`w-14 h-14 ${accent.soft} rounded-lg flex items-center justify-center mb-6`}>
                      {Icon && <Icon className={`w-7 h-7 ${accent.text}`} />}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{f.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{f.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-32 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-5xl sm:text-6xl font-bold mb-6 text-gray-900">Cómo Funciona</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-12 max-w-5xl mx-auto">
              {data.howItWorks.map((step, i) => (
                <div key={i} className="text-center">
                  <div className={`w-16 h-16 ${accent.bg} text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6`}>
                    {i + 1}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-32 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-5xl sm:text-6xl font-bold text-white mb-8">
                Conoce {data.name} en una demo personalizada
              </h2>
              <p className="text-xl text-gray-300 mb-12 font-light">
                Agenda una reunión y te mostramos cómo se adapta a tu operación en minutos.
              </p>
              <Button
                size="lg"
                className={`${accent.bg} ${accent.hover} text-white px-10 py-7 text-base rounded font-semibold transition-all duration-200 inline-flex items-center gap-2`}
                onClick={() => (window.location.href = '/demo')}
                data-testid={`${data.slug}-cta-footer`}
              >
                Solicitar Demo
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default ModulePage;
